import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import DrugImageView from './DrugImageView';
import { Drug, DrugSearchResult } from '../../../common/models/types';

export interface DrugImageOptions {
  drugsData?: Drug[];
  pillsData?: DrugSearchResult[];
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    drugImage: {
      setDrugImage: (attributes: { itemCode: string; src: string }) => ReturnType;
    };
  }
}

export const DrugImage = Node.create<DrugImageOptions>({
  name: 'drugImage',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return {
      drugsData: undefined,
      pillsData: undefined,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      itemCode: {
        default: null,
        parseHTML: element => element.getAttribute('data-itemcode'),
        renderHTML: attributes => {
          if (!attributes.itemCode) {
            return {};
          }
          return {
            'data-itemcode': attributes.itemCode,
          };
        },
      },
      src: {
        default: null,
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes.src) {
            return {};
          }
          return {
            src: attributes.src,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        // src가 있는 약품 이미지
        tag: 'img[src*="phai/drugs/pictures"]',
        getAttrs: (dom) => {
          const src = (dom as HTMLElement).getAttribute('src');
          let itemCode = (dom as HTMLElement).getAttribute('data-itemcode') || '';

          if (!itemCode && src) {
            const itemCodeMatch = src.match(/\/([A-Za-z0-9]+)\.jpg$/);
            itemCode = itemCodeMatch ? itemCodeMatch[1] : '';
          }

          return { itemCode, src };
        },
      },
      {
        // src가 없어도 data-itemcode만으로 약품 노드 인식
        tag: 'img[data-itemcode]',
        getAttrs: (dom) => {
          const src = (dom as HTMLElement).getAttribute('src') || null;
          const itemCode = (dom as HTMLElement).getAttribute('data-itemcode') || '';

          return { itemCode, src };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs: Record<string, any> = {
      'data-itemcode': node.attrs.itemCode,
      alt: '약품이미지',
      style: 'width: 100px; object-fit: contain; margin: 0 8px; vertical-align: middle;',
    };

    if (node.attrs.src) {
      attrs.src = node.attrs.src;
    }

    return [
      'img',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, attrs),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DrugImageView);
  },

  addCommands() {
    return {
      setDrugImage:
        (attributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          });
        },
    };
  },
});
